// Local progress and settings. Everything stays in this browser.

export interface LevelProgress {
  stars: [boolean, boolean, boolean];
  bestSeconds: number | null;
  runs: { agency: number; safety: number; won: boolean; at: number }[];
}

export interface Settings {
  reducedMotion: boolean;
  labels: boolean;
  voicePass: string;
  voiceMode: 'auto' | 'gpt-live' | 'browser';
}

const PKEY = 'br.progress.v1';
const SKEY = 'br.settings.v1';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

export function loadProgress(): Record<string, LevelProgress> {
  return read<Record<string, LevelProgress>>(PKEY, {});
}

export function recordRun(levelId: string, r: { stars: [boolean, boolean, boolean]; seconds: number; agency: number; safety: number; won: boolean }) {
  const all = loadProgress();
  const cur: LevelProgress = all[levelId] ?? { stars: [false, false, false], bestSeconds: null, runs: [] };
  cur.stars = [cur.stars[0] || r.stars[0], cur.stars[1] || r.stars[1], cur.stars[2] || r.stars[2]];
  if (r.won && (cur.bestSeconds === null || r.seconds < cur.bestSeconds)) cur.bestSeconds = Math.round(r.seconds);
  cur.runs = [...cur.runs, { agency: r.agency, safety: r.safety, won: r.won, at: Date.now() }].slice(-12);
  all[levelId] = cur;
  try {
    localStorage.setItem(PKEY, JSON.stringify(all));
  } catch {
    /* storage full or blocked: progress just won't persist */
  }
  return cur;
}

export function loadSettings(): Settings {
  const prefersReduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  return read<Settings>(SKEY, { reducedMotion: prefersReduced, labels: false, voicePass: '', voiceMode: 'auto' });
}

export function saveSettings(s: Settings) {
  try {
    localStorage.setItem(SKEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export const RANKS: { name: string; at: number }[] = [
  { name: 'Intern', at: 0 },
  { name: 'Engineer', at: 3 },
  { name: 'Senior engineer', at: 7 },
  { name: 'Staff engineer', at: 12 },
  { name: 'Principal engineer', at: 17 },
  { name: 'AI-native', at: 21 },
];

/** Career rank from stars earned across the campaign. */
export function rank(levelIds: string[]): { name: string; stars: number; total: number; next: { name: string; at: number } | null } {
  const p = loadProgress();
  let stars = 0;
  for (const id of levelIds) stars += (p[id]?.stars ?? []).filter(Boolean).length;
  let cur = RANKS[0];
  for (const r of RANKS) if (stars >= r.at) cur = r;
  const next = RANKS.find((r) => r.at > stars) ?? null;
  return { name: cur.name, stars, total: levelIds.length * 3, next };
}
